import React, { FormEvent, useState, useEffect, useRef } from "react";

import { useCreateUser, useGetUsers, useGetTitleCompanies } from "hooks";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Select,
  FormControl,
  MenuItem,
} from "@mui/material";
import { Button, InputField, Text } from "components/common";
import { ViewWrapper } from "components/wrappers";
import { notification } from "antd";
import CrossDrawerIcon from "../../assets/images/CrossDrawerIcon.svg";
import CreateUserIcon from "../../assets/images/CreateUserIcon.svg";
import { TableData, TableDataItem, TableDataItemValue } from "types/components";
import { UsersInStore } from "types/store";
import { SearchOutlined } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useUserStore } from "store";
import { InputLabel } from "@mui/material";
import Modal from "@mui/material/Modal";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import closeIcon from "../../assets/images/closeIcon.svg";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import { INITIAL_USERS_TABLE_DATA } from "components/user/ManageUsersTable/consts";
import SaveOrNotModal from "components/common/SaveOrNotModal/SaveOrNotModal";
import { TitleCompanyTable } from "components/title_company/TitleCompanyTable";
import Multiselect from "multiselect-react-dropdown";
import { useCreateTitleCompany } from "hooks/useCreateTitleCompany";
import { updateTitleCompany } from "services/TitleService";
import TitleCompaniesIcon from "../../assets/images/CreateCompanyIcon.svg";

interface Filter {
  column: string;
  operator: string;
  value: string;
}
type OptionType = {
  name: string;
  id: number;
};

export const TitleCompanies: React.FC = () => {
  const { getUsers } = useGetUsers();
  const { getTitleCompanies } = useGetTitleCompanies();
  const {
    createTitleCompany,
    loading,
    error: createTitleError,
    errorData: errorData,
  } = useCreateTitleCompany();
  const { users } = useUserStore();
  const [usersTableData, setUsersTableData] = useState<TableData>({
    ...INITIAL_USERS_TABLE_DATA,
  });
  const [selectedTitleCompany, setSelectedTitleCompany] = useState<any>({});
  const [territory, setTerritory] = useState<string[]>([]);
  const [email, setEmail] = useState<string>("");
  const [phone_no, setPhoneNumber] = useState<string>("");
  const [company_address, setCompanyAddress] = useState<string>("");
  const [title_company, setTitleCompanyName] = useState<string>("");
  const [api, contextHolder] = notification.useNotification();
  const [openUserDrawer, setOpenUserDrawer] = useState(false);
  const [openUpdateUserDrawer, setOpenUpdateUserDrawer] = useState(false);
  const [keyword, setKeyword] = useState<string>("");
  const [modalTabs, setModalTabs] = useState<string>("information");
  const [refresh, setRefresh] = useState<boolean>(false);
  const [open, setOpen] = React.useState(false);
  const userFilterButton = useRef(null);
  const [isChanges, setIsChanges] = useState(false);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  const mapUsersToTableData = (
    data: UsersInStore,
    initialTableData: TableData,
    setStateFunc: React.Dispatch<React.SetStateAction<TableData>>
  ) => {
    const newTableData: TableData = { ...initialTableData, data: [] };

    data?.forEach((item) => {
      const tableItem: TableDataItem = {};
      // Make sure every tableItem contains 'id' field
      tableItem.id = item.id;

      newTableData.headers.forEach((header) => {
        const key = header.key as keyof UsersInStore;
        tableItem[key] = item[key] as TableDataItemValue;
      });
      newTableData.data.push(tableItem);
    });
    setStateFunc({ ...newTableData });
  };

  const handleOpen = () => {
    if (userFilterButton.current) {
      const rect = userFilterButton;
      // @ts-ignore
      const top = rect.current["offsetTop"];
      // @ts-ignore
      const left = rect.current["offsetLeft"];
      setPosition({
        top: top + 50,
        left: left - 180,
      });
    }
    setOpen(true);
  };
  const [filters, setFilters] = useState<Filter[]>([
    { column: "", operator: "", value: "" },
  ]);

  const toggleDrawer = (openUserDrawer: any) => () => {
    setOpenUserDrawer(openUserDrawer);
    setSelectedTitleCompany("");
    setTitleCompanyName("");
    setCompanyAddress("");
    setPhoneNumber("");
    setEmail("");
    setTerritory([]);
  };

  const toggleUpdateDrawer = (openUserDrawer: boolean) => {
    setOpenUpdateUserDrawer(openUserDrawer);
    setSelectedTitleCompany("");
    setTitleCompanyName("");
    setCompanyAddress("");
    setEmail("");
    setPhoneNumber("");
  };

  useEffect(() => {
    mapUsersToTableData(users, INITIAL_USERS_TABLE_DATA, setUsersTableData);
  }, [users]);

  useEffect(() => {
    if (errorData) {
      setConfirmationModal("duplicate-value");
    }
  }, [errorData]);

  const handleAddTitleCompany = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      email.length > 0 &&
      title_company.length > 0 &&
      company_address.length > 0 &&
      phone_no.length > 0 &&
      territory.length > 0
    ) {
      const res = await createTitleCompany({
        email,
        phone_no,
        company_address,
        title_company,
        territory,
      });

      if (!res) {
        console.error("Error creating title company:", errorData);

        return;
      }

      api.success({
        message: `Company Added Successfully`,
        placement: "bottomRight",
      });

      setOpenUserDrawer(false);
      setRefresh(true);
      setCompanyAddress("");
      setTitleCompanyName("");
      setPhoneNumber("");
      setEmail("");
    }
  };

  // Update a specific filter
  const handleUpdateFilter = (
    index: number,
    field: keyof Filter,
    value: string
  ) => {
    const updatedFilters = [...filters];
    updatedFilters[index][field] = value;
    setFilters(updatedFilters);
  };

  // Remove a specific filter row
  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleClose = () => setOpen(false);

  // Clear all filters
  const handleCleanAll = () => {
    setFilters([{ column: "", operator: "", value: "" }]);
  };

  const handleAddFilter = () => {
    setFilters([...filters, { column: "", operator: "", value: "" }]);
  };

  useEffect(() => {
    if (!open) {
      getUsers({ filters });
    }
  }, [open]);

  useEffect(() => {
    const res = getTitleCompanies();
  }, [refresh]);

  const updateCompanyDetails = async () => {
    try {
      const companyId = selectedTitleCompany?.id;
      const demographics = {
        title_company,
        company_address,
        phone_no,
        email,
        territory,
      };

      const result = await updateTitleCompany({
        companyId,
        demographics,
      });
      if (!result) return;
      api.success({
        message: `Company Updated Successfully`,
        placement: "bottomRight",
      });
      setRefresh(true);
      setOpenUpdateUserDrawer(false);
      setConfirmationModal("");
      setSelectedTitleCompany({});
      setTitleCompanyName("");
      setCompanyAddress("");
      setPhoneNumber("");
      setTerritory([]);
      setEmail("");
      setIsChanges(false);
      toggleUpdateDrawer(false);
    } catch (error) {
      console.error("Error updating demographics:", error);
    }
  };

  useEffect(() => {
    // if (keyword) {
    getUsers({ keyword });
    // }
  }, [keyword]);

  useEffect(() => {
    if (refresh) {
      getUsers({});
    }
    setRefresh(false);
  }, [refresh]);

  const [confirmationModal, setConfirmationModal] = useState("");

  const handleCheckChanges = () => {
    if (isChanges) {
      setConfirmationModal("changes");
    } else {
      toggleUpdateDrawer(false);
    }
  };

  const texasCities = [
    "Houston",
    "San Antonio",
    "Dallas",
    "Austin",
    "Fort Worth",
    "El Paso",
    "Arlington",
    "Corpus Christi",
    "Plano",
    "Lubbock",
    "Laredo",
    "Irving",
    "Garland",
    "Frisco",
    "Amarillo",
    "McKinney",
    "Grand Prairie",
    "Brownsville",
    "Killeen",
    "Pasadena",
    "Mesquite",
    "McAllen",
    "Carrollton",
    "Waco",
    "Denton",
    "Midland",
    "Abilene",
    "Beaumont",
    "Round Rock",
    "Odessa",
    "Richardson",
    "Tyler",
    "Lewisville",
    "College Station",
    "Pearland",
    "San Angelo",
    "Allen",
    "League City",
    "Longview",
    "Edinburg",
  ];

  const options: OptionType[] = texasCities.map((city, index) => ({
    name: city,
    id: index + 1,
  }));

  const onSelect = (selectedList: OptionType[]) => {
    const names = selectedList.map((item) => item.name);
    setTerritory(names);
  };

  const onRemove = (selectedList: OptionType[]) => {
    const names = selectedList.map((item) => item.name);
    setTerritory(names);
  };
  useEffect(() => {
    if (selectedTitleCompany) {
      setEmail(selectedTitleCompany.email || "");
      setTerritory(selectedTitleCompany.territory || []);
      setPhoneNumber(selectedTitleCompany.phone_no || "");
      setCompanyAddress(selectedTitleCompany.company_address || "");
      setTitleCompanyName(selectedTitleCompany.title_company || "");
    } else {
      setEmail("");
      setTerritory([]);
      setPhoneNumber("");
      setCompanyAddress("");
      setTitleCompanyName("");
    }
  }, [selectedTitleCompany]);

  return (
    <ViewWrapper title="Title Companies">
      {contextHolder}
      <Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { sm: "center" },
            justifyContent: "space-between",
            marginBottom: "20px",
            gap: { xs: "10px", md: "0px" },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "60%" },
              gap: { xs: "5px", md: "10px" },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              sx={{ width: "100%", maxWidth: "400px" }}
              id="standard-bare"
              defaultValue="Search"
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <IconButton>
                    <SearchOutlined />
                  </IconButton>
                ),
                style: {
                  borderRadius: "30px",
                  height: "35px",
                  fontFamily: "PlusJakartaSans-Regular",
                },
              }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            {/* Desktop Filter Button */}
            {/* <Button
              variant="outlined"
              sx={{
                display: { xs: "none", sm: "flex" },
                backgroundColor: "black",
                textTransform: "none",
                borderRadius: "30px",
                color: "white",
                border: "1px solid #DDDDDD",
                fontFamily: "PlusJakartaSans-Regular",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                  border: "1px solid #DDDDDD",
                },
              }}
              startIcon={<FilterListIcon />}
              onClick={handleOpen}
            >
              Filter
            </Button> */}
            <Box ref={userFilterButton} sx={{ backgroundColor: "green" }}></Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "end" }}>
            {/* Mobile Filter Button */}
            <Button
              variant="outlined"
              sx={{
                width: "fit-content",
                backgroundColor: "black",
                textTransform: "none",
                borderRadius: "30px",
                color: "white",
                border: "1px solid #DDDDDD",
                display: { xs: "flex", sm: "none" },
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                  border: "1px solid #DDDDDD",
                },
              }}
              onClick={handleOpen}
            >
              <FilterListIcon />
            </Button>

            {/*Desktop User button */}
            <Button
              variant="outlined"
              sx={{
                backgroundColor: "black",
                textTransform: "none",
                whiteSpace: "nowrap",
                borderRadius: "30px",
                color: "white",
                border: "1px solid #DDDDDD",
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                "&:hover": {
                  backgroundColor: "black",
                  border: "1px solid #AAAAAA",
                  color: "#fff",
                },
              }}
              startIcon={
                <img
                  src={TitleCompaniesIcon}
                  alt="Create User Icon"
                  style={{ height: 20 }}
                />
              }
              onClick={toggleDrawer(true)}
            >
              Create Company
            </Button>

            {/*Mobile User button */}
            <Button
              variant="outlined"
              sx={{
                backgroundColor: "black",
                textTransform: "none",
                borderRadius: "30px",
                color: "white",
                border: "1px solid #DDDDDD",
                display: { xs: "flex", sm: "none" },
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={toggleDrawer(true)}
            >
              <img
                src={CreateUserIcon}
                alt="Create User Icon"
                style={{ width: 25 }}
              />
            </Button>
          </Box>
        </Box>
        <Text marginTop="10px">{users?.length} Results</Text>

        <TitleCompanyTable
          setOpenUpdateUserDrawer={setOpenUpdateUserDrawer}
          setModalTabs={setModalTabs}
          setSelectedTitleCompany={setSelectedTitleCompany}
        />
        {/* Company Create */}
        <Drawer
          anchor="right"
          slotProps={{
            backdrop: {
              style: {
                backgroundColor: "#11111180 ",
                backdropFilter: "blur(2px)",
                // zIndex: "99999",
              },
            },
          }}
          open={openUserDrawer}
          onClose={toggleDrawer(false)}
          sx={{
            "& .MuiDrawer-paper": {
              borderRadius: "16px",
              height: "97.5vh",
              margin: "10px",
              padding: "20px",

              width: { xs: "94%", sm: "75%", md: "55%", lg: "35%" },
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              padding: "25px",
              typography: "body1",
            }}
          >
            <Box
              marginBottom="20px"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text
                sx={{
                  fontFamily: "PlusJakartaSans-Regular",
                  fontSize: "24px",
                  fontWeight: "600",
                }}
                // paddingLeft={"10px"}
              >
                Create Title Company
              </Text>
              <img
                src={CrossDrawerIcon}
                style={{ height: "30px", cursor: "pointer" }}
                onClick={toggleDrawer(false)}
              />
            </Box>

            <form onSubmit={handleAddTitleCompany}>
              <Stack spacing={2}>
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Company Name
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  placeholder="Enter Company Name...."
                  required
                  value={title_company}
                  onChange={setTitleCompanyName}
                />
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Company Address
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  placeholder="Enter Company Address...."
                  required
                  value={company_address}
                  onChange={setCompanyAddress}
                />
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Phone Number
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  placeholder="Enter Phone Number...."
                  required
                  value={phone_no}
                  onChange={setPhoneNumber}
                />
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Email
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  placeholder="Enter Email Address...."
                  required
                  value={email}
                  onChange={setEmail}
                />
                <span
                  style={{
                    fontFamily: "PlusJakartaSans-Regular",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Territory
                </span>
                <Multiselect
                  options={options}
                  selectedValues={options.filter((option) =>
                    territory.includes(option.name)
                  )}
                  customCloseIcon={
                    <span
                      style={{
                        padding: "0px 5px",
                        fontFamily: "ariel",
                        fontWeight: "700",
                        color: "black",
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                    >
                      ×
                    </span>
                  }
                  onSelect={onSelect}
                  onRemove={onRemove}
                  displayValue="name"
                  placeholder="Select Territory...."
                  style={{
                    chips: {
                      background: "lightgray",
                      color: "black",
                      borderRadius: 20,
                      marginTop: 5,
                    },
                    multiselectContainer: {
                      color: "black",
                    },
                    searchBox: {
                      border: "1px solid lightgray",
                      borderRadius: "25px",
                      padding: "6px 10px",
                    },
                    optionContainer: {
                      border: "1px solid #ccc",
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: "white",
                      boxShadow: "5px 5px 8px 2px lightgray",
                      fontSize: "14px",
                    },
                    option: {
                      borderBottom: "1px solid #ccc",
                      color: "gray",
                      backgroundColor: "white",
                      padding: "5px 10px",
                      fontSize: "14px",
                    },
                  }}
                />
                <Button
                  style={{
                    backgroundColor: "#FFE500",
                    borderRadius: "40px",
                    color: "black",
                    height: "35px",
                    width: "fit-content",
                    fontWeight: "700",
                    padding: "3px 15px 0px 15px",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    marginTop: "20px",
                  }}
                  type="submit"
                  // disabled={createUserLoading}
                >
                  Save
                </Button>
              </Stack>
            </form>
          </Box>
        </Drawer>

        {/* User update */}
        <SaveOrNotModal
          onDiscardClick={() => {
            toggleUpdateDrawer(false);
            setConfirmationModal("");
            setIsChanges(false);
          }}
          onSaveClick={() => {
            if (confirmationModal === "changes") {
              updateCompanyDetails();
            } else {
              setOpenUpdateUserDrawer(false);
              setConfirmationModal("");
            }
          }}
          textMsg={
            confirmationModal == "changes"
              ? "You have unsaved changes. Please save or discard them before continuing."
              : errorData?.response?.data.payload.message ||
                "Some value already exist!"
          }
          saveButtonText={
            confirmationModal == "changes" ? "Save Changes" : "Ok"
          }
          isOpen={confirmationModal !== ""}
          singleButton={confirmationModal == "duplicate-value" ? false : true}
        />
        <Drawer
          anchor="right"
          slotProps={{
            backdrop: {
              style: {
                backgroundColor: "#11111180 ",
                backdropFilter: "blur(2px)",
                // zIndex: "99999",
              },
            },
          }}
          open={openUpdateUserDrawer}
          onClose={handleCheckChanges}
          sx={{
            "& .MuiDrawer-paper": {
              borderRadius: "16px",
              height: "97.5vh",
              margin: "10px",
              padding: "20px",

              width: { xs: "94%", sm: "75%", md: "55%", lg: "35%" },
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              padding: "25px",
              typography: "body1",
            }}
          >
            {/* Update Modal Header */}
            <Box
              marginBottom="20px"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text
                sx={{
                  fontFamily: "PlusJakartaSans-Regular",
                  fontSize: "24px",
                  fontWeight: "600",
                }}
              >
                Company Details
              </Text>
              <img
                src={CrossDrawerIcon}
                style={{ height: "30px", cursor: "pointer" }}
                onClick={handleCheckChanges}
              />
            </Box>
            {/* Update Modal Tab Button */}
            {/* <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "45px",
                gap: "10px",
                borderBottom: "2px solid lightgray",
              }}
            >
              <Button
                sx={{
                  color: modalTabs == "information" ? "black" : "lightgray",
                  borderBottom:
                    modalTabs == "information" ? "2px solid black" : "",
                  borderRadius: 0,
                  marginBottom: "-2px",
                }}
                onClick={() => setModalTabs("information")}
              >
                Information
              </Button>
              <Button
                sx={{
                  color: modalTabs == "address" ? "black" : "lightgray",
                  borderBottom: modalTabs == "address" ? "2px solid black" : "",
                  borderRadius: 0,
                  marginBottom: "-2px",
                }}
                onClick={() => setModalTabs("address")}
              >
                Address
              </Button>
            </Box> */}
            {/* Update Modal Form */}
            <form onSubmit={updateCompanyDetails}>
              <Stack spacing={1}>
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        // backgroundColor:"red",
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Company Name
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  required
                  placeholder="Enter Company Name..."
                  value={title_company}
                  onChange={(value: any) => {
                    setTitleCompanyName(value);
                    setIsChanges(true);
                  }}
                />
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        // backgroundColor:"red",
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Company Address
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  required
                  placeholder="Enter Company Address"
                  value={company_address}
                  onChange={(value: any) => {
                    setCompanyAddress(value);
                    setIsChanges(true);
                  }}
                />
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        // backgroundColor:"red",
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Phone Number
                    </span>
                  }
                  type="email"
                  variant="outlined"
                  required
                  placeholder="Enter Phone Number"
                  value={phone_no}
                  onChange={(value: any) => {
                    setPhoneNumber(value);
                    setIsChanges(true);
                  }}
                />
                <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        // backgroundColor:"red",
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Email
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  required
                  placeholder="Enter Email Address"
                  value={email}
                  onChange={(value: any) => {
                    setEmail(value);
                    setIsChanges(true);
                  }}
                />
                {/* <InputField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // Styling the border
                      "& fieldset": {
                        // backgroundColor:"red",
                        borderColor: "#E0E0E0",
                      },
                      // On hover
                      "&:hover fieldset": {
                        borderColor: "#D6D6D6",
                      },
                      // On focus
                      "&.Mui-focused fieldset": {
                        borderColor: "#CFCFCF",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "PlusJakartaSans-Regular",
                      fontSize: "12px",
                      fontWeight: "600",
                    },
                  }}
                  style={{ marginBottom: "10px" }}
                  label={
                    <span
                      style={{
                        fontFamily: "PlusJakartaSans-Regular",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Territory
                    </span>
                  }
                  type="number"
                  variant="outlined"
                  required
                  placeholder="Enter Zip Code"
                  value={zipCode}
                  onChange={(value: any) => {
                    setZipCode(value);
                    setIsChanges(true);
                  }}
                /> */}
                <span
                  style={{
                    fontFamily: "PlusJakartaSans-Regular",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Territory
                </span>
                <Multiselect
                  options={options}
                  selectedValues={options.filter((option) =>
                    territory.includes(option.name)
                  )}
                  onSelect={onSelect}
                  onRemove={onRemove}
                  displayValue="name"
                  customCloseIcon={
                    <span
                      style={{
                        padding: "0px 5px",
                        fontFamily: "ariel",
                        fontWeight: "700",
                        color: "black",
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                    >
                      ×
                    </span>
                  }
                  placeholder="Select Territory"
                  style={{
                    chips: {
                      background: "lightgray",
                      color: "black",
                      borderRadius: 20,
                      marginTop: 5,
                    },
                    multiselectContainer: {
                      color: "black",
                    },
                    searchBox: {
                      border: "1px solid lightgray",
                      borderRadius: "25px",
                      padding: "6px 10px",
                    },
                    optionContainer: {
                      border: "1px solid #ccc",
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: "white",
                      boxShadow: "5px 5px 8px 2px lightgray",
                      fontSize: "14px",
                    },
                    option: {
                      borderBottom: "1px solid #ccc",
                      color: "gray",
                      backgroundColor: "white",
                      padding: "5px 10px",
                      fontSize: "14px",
                    },
                  }}
                />

                <Button
                  style={{
                    backgroundColor: "#FFE500",
                    borderRadius: "40px",
                    color: "black",
                    height: "35px",
                    width: "fit-content",
                    fontWeight: "700",
                    padding: "3px 15px 0px 15px",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    marginTop: "20px",
                  }}
                  type="button"
                  // disabled={createUserLoading}
                  onClick={updateCompanyDetails}
                >
                  Update Changes
                </Button>
              </Stack>
            </form>
          </Box>
        </Drawer>

        {/* Filter Modal */}
        <Modal
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          BackdropProps={{
            sx: {
              backgroundColor: "transparent",
              backdropFilter: { xs: "blur(2px)", lg: "blur(0px)" },
            }, // Removes opacity
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: "50%", lg: `${position?.top}px` },
              left: { xs: "50%", lg: `${position?.left + 50}px` },
              transform: {
                xs: "translate(-50%, -50%)",
                lg: "translate(0%, 0%)",
              },
              width: { xs: "95vw", sm: "88vw", md: "75vw", lg: "57vw" },
              maxWidth: 900,
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 3,
            }}
          >
            <Box
              display="flex"
              justifyContent="start"
              alignItems="center"
              marginTop="20px"
              sx={{
                px: 2,
              }}
            >
              <p
                style={{
                  margin: 0,
                  paddingBottom: "20px",
                  fontFamily: "PlusJakartaSans-SemiBold",
                }}
              >
                Filter
              </p>
            </Box>
            <Box sx={{ justifyContent: "center" }}>
              <Box sx={{ maxHeight: "350px", overflowX: "auto", px: 2 }}>
                {filters.map((filter, index) => (
                  <>
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "end",
                        mb: 2,
                        gap: "10px",
                        pb: 2,
                        borderBottom: {
                          xs: filters?.length > 1 ? "1px solid lightgray" : "",
                          sm: "none",
                        },
                      }}
                    >
                      {/* <FormControl>
                      <InputLabel
                        htmlFor="first-name"
                        sx={{
                          color: "black",
                          fontFamily: "PlusJakartaSans-SemiBold",
                          marginTop: "-28px",
                        }}
                      >
                        Column
                      </InputLabel>
                      <Box sx={{ marginBottom: "15px" }} />
                      <TextField
                        id={`column-${index}`}
                        value={filter.column}
                        onChange={(e) =>
                          handleUpdateFilter(index, "column", e.target.value)
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton>
                              <ArrowDropDownIcon sx={{ color: "black" }} />
                            </IconButton>
                          ),
                          style: {
                            borderRadius: "30px",
                            height: "35px",
                            width: "100%",
                            fontSize: "15px",
                            fontFamily: "PlusJakartaSans-Regular",
                          },
                        }}
                        sx={{ marginLeft: "10px", marginRight: "10px" }}
                      />
                    </FormControl> */}
                      {filters?.length > 1 && (
                        <img
                          src={closeIcon}
                          alt="Close Icon"
                          style={{ cursor: "pointer", marginBottom: "4px" }}
                          onClick={() => handleRemoveFilter(index)}
                        />
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          width: { xs: "100%", sm: "33.33%" },
                        }}
                      >
                        <InputLabel
                          htmlFor="first-name"
                          sx={{
                            color: "black",
                            fontFamily: "PlusJakartaSans-SemiBold",
                          }}
                        >
                          Column
                        </InputLabel>
                        <FormControl>
                          <Select
                            id={`column-${index}`}
                            value={filter.column}
                            onChange={(e) =>
                              handleUpdateFilter(
                                index,
                                "column",
                                e.target.value
                              )
                            }
                            sx={{
                              borderRadius: "30px",
                              height: "35px",
                              width: "100%",
                              fontSize: "15px",
                              fontFamily: "PlusJakartaSans-Regular",
                            }}
                            IconComponent={() => (
                              <ArrowDropDownIcon sx={{ color: "black" }} />
                            )}
                          >
                            {/* {usersTableData?.headers?.map((h) => (
                              <MenuItem key={h.key} value={h.key}>
                                {h.title}
                              </MenuItem>
                            ))} */}

                            <MenuItem value="given_name">Given Name</MenuItem>
                            <MenuItem value="family_name">Family Name</MenuItem>
                            <MenuItem value="email_address">Email</MenuItem>
                            <MenuItem value="address_line_1">
                              Address Line 1
                            </MenuItem>
                            <MenuItem value="address_line_2">
                              Address Line 2
                            </MenuItem>
                            <MenuItem value="city">City</MenuItem>
                            <MenuItem value="state">State</MenuItem>
                            <MenuItem value="zip">Zip</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          width: { xs: "100%", sm: "33.33%" },
                        }}
                      >
                        <InputLabel
                          id={`operator-label-${index}`}
                          sx={{
                            color: "black",
                            fontFamily: "PlusJakartaSans-SemiBold",
                          }}
                        >
                          Operator
                        </InputLabel>
                        <FormControl>
                          <Select
                            labelId={`operator-label-${index}`}
                            id={`operator-${index}`}
                            value={filter.operator}
                            onChange={(e) =>
                              handleUpdateFilter(
                                index,
                                "operator",
                                e.target.value
                              )
                            }
                            sx={{
                              borderRadius: "30px",
                              height: "35px",
                              width: "100%",
                              fontSize: "15px",
                              fontFamily: "PlusJakartaSans-Regular",
                            }}
                            IconComponent={() => (
                              <ArrowDropDownIcon sx={{ color: "black" }} />
                            )}
                          >
                            <MenuItem value="equals">=</MenuItem>
                            <MenuItem value="not equals">!=</MenuItem>
                            <MenuItem value="contain">Contain</MenuItem>
                            <MenuItem value="not contain">
                              {" "}
                              Not Contain
                            </MenuItem>
                            <MenuItem value="start with">Starts With</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* <FormControl>
                      <InputLabel
                        htmlFor="first-name"
                        sx={{
                          color: "black",
                          fontFamily: "PlusJakartaSans-SemiBold",
                          marginTop: { sm:"-28px"},
                        }}
                      >
                        Operator
                      </InputLabel>
                      <Box sx={{ marginBottom: "15px" }} />
                      <TextField
                        id={`operator-${index}`}
                        value={filter.operator}
                        onChange={(e) =>
                          handleUpdateFilter(index, "operator", e.target.value)
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton>
                              <ArrowDropDownIcon sx={{ color: "black" }} />
                            </IconButton>
                          ),
                          style: {
                            borderRadius: "30px",
                            height: "35px",
                            width: "100%",
                            fontSize: "15px",
                            fontFamily: "PlusJakartaSans-Regular",
                          },
                        }}
                        sx={{ marginLeft: "10px", marginRight: "10px" }}
                      />{" "}
                    </FormControl> */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          width: { xs: "100%", sm: "33.33%" },
                        }}
                      >
                        <InputLabel
                          htmlFor="first-name"
                          sx={{
                            color: "black",
                            fontFamily: "PlusJakartaSans-SemiBold",
                          }}
                        >
                          Value
                        </InputLabel>
                        <FormControl>
                          <TextField
                            id={`value-${index}`}
                            value={filter.value}
                            onChange={(e) =>
                              handleUpdateFilter(index, "value", e.target.value)
                            }
                            InputProps={{
                              style: {
                                borderRadius: "30px",
                                height: "35px",
                                fontSize: "15px",
                                fontFamily: "PlusJakartaSans-Regular",
                              },
                            }}
                            sx={{ marginRight: "10px" }}
                          />
                        </FormControl>
                      </Box>
                    </Box>
                  </>
                ))}
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginTop="20px"
                sx={{ px: 2, pb: 3 }}
              >
                <Button
                  variant="outlined"
                  style={{
                    backgroundColor: "#FFE500",
                    textTransform: "none",
                    borderRadius: "30px",
                    color: "black",
                    border: "1px solid #DDDDDD",
                    fontSize: "15px",
                    fontFamily: "PlusJakartaSans-Regular",
                  }}
                  startIcon={<ControlPointIcon />}
                  onClick={handleAddFilter}
                >
                  Add Filter
                </Button>

                <p
                  style={{
                    cursor: "pointer",
                    margin: 0,
                    textDecoration: "underline",
                    fontSize: "15px",
                    color: "black",
                    fontFamily: "PlusJakartaSans-Regular",
                  }}
                  onClick={handleCleanAll}
                >
                  Clean All
                </p>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Stack>
    </ViewWrapper>
  );
};
